import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DarkPage } from "@/components/app/PageHeader";
import { ChevronLeft, ChevronRight, Plus, X } from "lucide-react";

const TYPE_COLORS = {
  booking: "bg-[#00F5D4]/20 text-[#00F5D4] border-[#00F5D4]/30",
  leave: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  holiday: "bg-rose-500/20 text-rose-400 border-rose-500/30",
};

const TYPE_LABELS = { booking: "Project Booking", leave: "Leave", holiday: "Holiday" };

function daysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function startDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

function isoDate(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

export default function ResourceCalendar() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [bookings, setBookings] = useState([]);
  const [team, setTeam] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [drawer, setDrawer] = useState(null); // {date, booking?}
  const [form, setForm] = useState({ user_id: "", type: "booking", title: "", start_date: "", end_date: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const monthStart = isoDate(year, month, 1);
  const monthEnd = isoDate(year, month, daysInMonth(year, month));

  const load = useCallback(async () => {
    try {
      const [bRes, tRes] = await Promise.all([
        api.get(`/resource-calendar/bookings?start=${monthStart}&end=${monthEnd}`),
        api.get("/team/members"),
      ]);
      setBookings(bRes.data || []);
      setTeam(Array.isArray(tRes.data) ? tRes.data : []);
    } catch {
      toast.error("Failed to load calendar");
    }
  }, [monthStart, monthEnd]);

  useEffect(() => { load(); }, [load]);

  const prevMonth = () => { if (month === 0) { setYear(y => y - 1); setMonth(11); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setYear(y => y + 1); setMonth(0); } else setMonth(m => m + 1); };

  const bookingsForDay = (day) => {
    const d = isoDate(year, month, day);
    return bookings.filter(b => b.start_date <= d && b.end_date >= d && (!selectedUser || b.user_id === selectedUser));
  };

  const openCreate = (day) => {
    const d = isoDate(year, month, day);
    setForm({ user_id: selectedUser || (team[0]?.user_id || ""), type: "booking", title: "", start_date: d, end_date: d, notes: "" });
    setDrawer({ date: d });
  };

  const openEdit = (b) => {
    setForm({ ...b });
    setDrawer({ date: b.start_date, booking: b });
  };

  const save = async () => {
    if (!form.title || !form.user_id || !form.start_date || !form.end_date) {
      toast.error("Fill in all required fields");
      return;
    }
    setSaving(true);
    try {
      if (drawer?.booking) {
        await api.put(`/resource-calendar/bookings/${drawer.booking.booking_id}`, form);
        toast.success("Booking updated");
      } else {
        await api.post("/resource-calendar/bookings", form);
        toast.success("Booking created");
      }
      setDrawer(null);
      load();
    } catch {
      toast.error("Failed to save booking");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (b) => {
    try {
      await api.delete(`/resource-calendar/bookings/${b.booking_id}`);
      toast.success("Booking deleted");
      setDrawer(null);
      load();
    } catch {
      toast.error("Failed to delete booking");
    }
  };

  const days = daysInMonth(year, month);
  const startDay = startDayOfMonth(year, month);
  const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

  const memberName = (uid) => {
    const m = team.find(t => t.user_id === uid);
    return m ? (m.name || m.email) : uid;
  };

  return (
    <DarkPage><div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Resource Calendar</h1>
          <p className="text-sm text-[#7d8590] mt-1">Book team members and manage leave</p>
        </div>
        <Button onClick={() => openCreate(today.getDate())} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
          <Plus className="w-4 h-4 mr-1" /> Add Booking
        </Button>
      </div>

      <div className="grid grid-cols-[200px_1fr] gap-6">
        {/* Team filter panel */}
        <div className="bg-[#0d1117] border border-[#21262d] rounded-lg p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#7d8590] mb-3">Team</div>
          <button
            onClick={() => setSelectedUser(null)}
            className={`w-full text-left px-3 py-2 rounded-md text-sm mb-1 transition-colors ${!selectedUser ? "bg-[#1c2128] text-white" : "text-[#7d8590] hover:bg-[#161b22]"}`}
          >
            All members
          </button>
          {team.map(m => (
            <button
              key={m.user_id}
              onClick={() => setSelectedUser(m.user_id === selectedUser ? null : m.user_id)}
              className={`w-full text-left px-3 py-2 rounded-md text-sm mb-1 transition-colors ${selectedUser === m.user_id ? "bg-[#1c2128] text-white" : "text-[#7d8590] hover:bg-[#161b22]"}`}
            >
              {m.name || m.email}
            </button>
          ))}
          <div className="mt-4 pt-4 border-t border-[#21262d]">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#7d8590] mb-2">Legend</div>
            {Object.entries(TYPE_LABELS).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 mb-1">
                <span className={`w-2 h-2 rounded-full ${k === "booking" ? "bg-[#00F5D4]" : k === "leave" ? "bg-amber-400" : "bg-rose-400"}`} />
                <span className="text-xs text-[#7d8590]">{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Calendar */}
        <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#21262d]">
            <button onClick={prevMonth} className="p-1 rounded hover:bg-[#161b22] text-[#7d8590] hover:text-white">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-white font-semibold">{MONTHS[month]} {year}</span>
            <div className="flex gap-2">
              <button onClick={() => { setYear(today.getFullYear()); setMonth(today.getMonth()); }} className="text-xs text-[#7d8590] hover:text-white px-2 py-1 rounded hover:bg-[#161b22]">Today</button>
              <button onClick={nextMonth} className="p-1 rounded hover:bg-[#161b22] text-[#7d8590] hover:text-white">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7">
            {DAYS.map(d => (
              <div key={d} className="text-center text-xs font-semibold text-[#7d8590] py-3 border-b border-[#21262d]">{d}</div>
            ))}
            {Array.from({ length: startDay }).map((_, i) => (
              <div key={`empty-${i}`} className="border-b border-r border-[#21262d] min-h-[100px]" />
            ))}
            {Array.from({ length: days }).map((_, i) => {
              const day = i + 1;
              const dayBookings = bookingsForDay(day);
              const isToday = day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
              return (
                <div
                  key={day}
                  className="border-b border-r border-[#21262d] min-h-[100px] p-1 cursor-pointer hover:bg-[#161b22]/50 transition-colors"
                  onClick={() => openCreate(day)}
                >
                  <div className={`text-xs font-medium mb-1 w-6 h-6 flex items-center justify-center rounded-full ${isToday ? "bg-[#00F5D4] text-[#030712]" : "text-[#7d8590]"}`}>
                    {day}
                  </div>
                  {dayBookings.slice(0, 3).map(b => (
                    <div
                      key={b.booking_id}
                      onClick={e => { e.stopPropagation(); openEdit(b); }}
                      className={`text-[10px] px-1.5 py-0.5 rounded border mb-0.5 truncate cursor-pointer ${TYPE_COLORS[b.type] || TYPE_COLORS.booking}`}
                    >
                      {b.title}
                    </div>
                  ))}
                  {dayBookings.length > 3 && <div className="text-[9px] text-[#7d8590]">+{dayBookings.length - 3} more</div>}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Drawer */}
      {drawer && (
        <div className="fixed inset-0 z-50 flex">
          <button className="flex-1 bg-black/50" onClick={() => setDrawer(null)} aria-label="Close" />
          <div className="w-96 bg-[#0d1117] border-l border-[#21262d] p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-white font-semibold">{drawer.booking ? "Edit Booking" : "New Booking"}</h2>
              <button onClick={() => setDrawer(null)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Team member *</label>
                <select
                  value={form.user_id}
                  onChange={e => setForm(f => ({ ...f, user_id: e.target.value }))}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm"
                >
                  {team.map(m => <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Type</label>
                <select
                  value={form.type}
                  onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm"
                >
                  {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Title *</label>
                <input
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm"
                  placeholder="e.g. Annual leave"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Start date *</label>
                  <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">End date *</label>
                  <input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Notes</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={3}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm resize-none" />
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <Button onClick={save} disabled={saving} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
                {saving ? "Saving…" : "Save"}
              </Button>
              {drawer.booking && (
                <Button onClick={() => remove(drawer.booking)} variant="destructive" className="px-3">Delete</Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div></DarkPage>
  );
}
